import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Smartphone, Share, Plus, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function detectPlatform() {
  if (typeof window === "undefined") return "other";
  const ua = window.navigator.userAgent || "";
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  return "desktop";
}

export default function InstallPWAButton({ variant = "primary", className = "" }) {
  const [canPrompt, setCanPrompt] = useState(!!window.__deferredInstallPrompt);
  const [installed, setInstalled] = useState(isStandalone());
  const [helpOpen, setHelpOpen] = useState(false);
  const platform = detectPlatform();

  useEffect(() => {
    const onAvailable = () => setCanPrompt(true);
    const onInstalled = () => {
      setCanPrompt(false);
      setInstalled(true);
    };
    window.addEventListener("pwa-install-available", onAvailable);
    window.addEventListener("pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("pwa-install-available", onAvailable);
      window.removeEventListener("pwa-installed", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    const promptEvent = window.__deferredInstallPrompt;
    if (promptEvent && typeof promptEvent.prompt === "function") {
      promptEvent.prompt();
      try {
        await promptEvent.userChoice;
      } catch (_) {}
      window.__deferredInstallPrompt = null;
      setCanPrompt(false);
    } else {
      // No prompt available (iOS Safari or already dismissed) → show manual help
      setHelpOpen(true);
    }
  };

  const baseClasses =
    variant === "ghost"
      ? "rounded-full text-[#4A3B32] hover:bg-[#FCF9F2] font-semibold"
      : "rounded-full bg-white border border-[#EADBCE] text-[#4A3B32] hover:bg-[#FCF9F2] font-bold shadow-sm";

  return (
    <>
      <Button
        type="button"
        onClick={handleClick}
        data-testid="install-pwa-button"
        className={`${baseClasses} ${className}`}
      >
        <Download className="w-4 h-4 mr-2" />
        Instalar app
      </Button>

      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="rounded-3xl border-[#EADBCE] sm:max-w-md" data-testid="install-help-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <Smartphone className="w-6 h-6 text-[#FF7878]" />
              Instalar Darkar Co.
            </DialogTitle>
            <DialogDescription className="text-[#8A796F]">
              Sigue estos pasos según tu dispositivo para tener la app en tu pantalla de inicio.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {platform === "ios" && (
              <Step icon={<Share className="w-5 h-5" />} title="iPhone / iPad (Safari)">
                <ol className="list-decimal pl-5 space-y-1.5 text-sm text-[#4A3B32]">
                  <li>Toca el botón <b>Compartir</b> <Share className="inline w-3.5 h-3.5" /> en la barra inferior.</li>
                  <li>Desplázate y selecciona <b>"Añadir a Inicio"</b>.</li>
                  <li>Confirma con <b>Añadir</b>.</li>
                </ol>
              </Step>
            )}

            {platform === "android" && (
              <Step icon={<Plus className="w-5 h-5" />} title="Android (Chrome)">
                <ol className="list-decimal pl-5 space-y-1.5 text-sm text-[#4A3B32]">
                  <li>Toca el menú <b>⋮</b> (arriba a la derecha).</li>
                  <li>Selecciona <b>"Instalar app"</b> o <b>"Añadir a pantalla de inicio"</b>.</li>
                  <li>Confirma <b>Instalar</b>.</li>
                </ol>
              </Step>
            )}

            {platform === "desktop" && (
              <Step icon={<Download className="w-5 h-5" />} title="Chrome / Edge en computadora">
                <ol className="list-decimal pl-5 space-y-1.5 text-sm text-[#4A3B32]">
                  <li>Mira el ícono de <b>instalación</b> <Download className="inline w-3.5 h-3.5" /> en la barra de direcciones (extremo derecho).</li>
                  <li>O entra al menú <b>⋮</b> → <b>"Instalar Darkar Co."</b>.</li>
                  <li>Confirma <b>Instalar</b>.</li>
                </ol>
                <p className="text-xs text-[#8A796F] mt-2">
                  Si no ves la opción, recarga la página o asegúrate de estar en Chrome/Edge (no Firefox).
                </p>
              </Step>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Step({ icon, title, children }) {
  return (
    <div className="rounded-2xl border border-[#EADBCE] p-4 bg-[#FCF9F2]">
      <div className="flex items-center gap-2 font-display font-bold text-[#4A3B32] mb-2">
        <span className="w-8 h-8 rounded-full bg-white border border-[#EADBCE] flex items-center justify-center text-[#FF7878]">
          {icon}
        </span>
        {title}
      </div>
      {children}
    </div>
  );
}
